/**
 * ADLAmendmentBanner
 * CCH Care — Phase 3
 *
 * Shown at the top of a section that has been amended after signing.
 * Lists amendment history for that section with field, reason, and who amended.
 */

import { useState } from 'react';
import { COLORS } from '../../config/constants';
import {
  formatShortDate, formatTime,
  formatAmendmentValue, formatAmendmentField,
} from '../../utils/formatters';
import TransparencyChip from '../ui/TransparencyChip';

export default function ADLAmendmentBanner({ amendments = [], section }) {
  const [expanded, setExpanded] = useState(false);

  // Filter to this section only
  const sectionAmendments = amendments.filter(a => a.section === section);
  if (sectionAmendments.length === 0) return null;

  const latest = sectionAmendments[0]; // Most recent first

  return (
    <div style={{
      background: COLORS.warningLight,
      border: `1px solid rgba(230,126,34,0.25)`,
      borderRadius: 10, marginBottom: 12,
      overflow: 'hidden',
    }}>
      {/* Summary row */}
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 14px', background: 'none', border: 'none',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>✏️</span>
          <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.warning }}>
            {sectionAmendments.length} Amendment{sectionAmendments.length > 1 ? 's' : ''}
          </span>
          <span style={{ fontSize: 12, color: COLORS.textMuted }}>
            — Last by {latest.first_name} {latest.last_name}
          </span>
        </div>
        <span style={{ fontSize: 12, color: COLORS.textMuted }}>
          {expanded ? '▲' : '▼'}
        </span>
      </button>

      {/* Expanded amendment list */}
      {expanded && (
        <div style={{ borderTop: `1px solid rgba(230,126,34,0.2)` }}>
          {sectionAmendments.map((a, i) => (
            <div
              key={a.adl_amendment_id ?? i}
              style={{
                padding: '10px 14px',
                borderBottom: i < sectionAmendments.length - 1 ? `1px solid rgba(230,126,34,0.15)` : 'none',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.text }}>
                  {formatAmendmentField(a.section, a.field_name)}
                </span>
                <span style={{ fontSize: 11, color: COLORS.textMuted }}>
                  {formatAmendmentDate(a.amended_at)}
                </span>
              </div>

              {/* Old → New value */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{
                  fontSize: 11, color: COLORS.danger,
                  background: COLORS.dangerLight, padding: '2px 6px', borderRadius: 4,
                  textDecoration: 'line-through',
                }}>
                  {formatAmendmentValue(a.section, a.field_name, a.old_value)}
                </span>
                <span style={{ fontSize: 11, color: COLORS.textMuted }}>→</span>
                <span style={{
                  fontSize: 11, color: COLORS.accent,
                  background: COLORS.accentLight, padding: '2px 6px', borderRadius: 4,
                }}>
                  {formatAmendmentValue(a.section, a.field_name, a.new_value)}
                </span>
              </div>

              {/* Reason */}
              <div style={{ fontSize: 12, color: COLORS.textMuted }}>
                <span style={{ fontWeight: 600 }}>Reason: </span>{a.reason}
              </div>
              <div style={{
                fontSize: 11, color: COLORS.textLight, marginTop: 2,
                display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
              }}>
                <span>By {a.first_name} {a.last_name}</span>
                {a.supervisor_amended && (
                  <TransparencyChip kind="supervisor_amended" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatAmendmentDate(ts) {
  if (!ts) return '';
  const d = new Date(ts);
  return formatShortDate(d)
    + ' ' + formatTime(d);
}
